import { FormattedMessage } from "react-intl";

import { Button, Card, Popconfirm, Space, Typography } from "antd";

import {
  CmtSyncRunAuthor,
  CmtSyncRunDuration,
  CmtSyncRunStartedAt,
  CmtSyncRunStatusTag,
  CmtSyncTriggerTag,
} from "@app/components/settings/CmtSyncRunDetails.tsx";
import { syncActionMessages } from "@app/components/settings/syncActionMessages.ts";
import { SyncActionStatus } from "@app/models/SyncAction.ts";

import styles from "./CmtSyncActionCard.module.scss";

interface CmtSyncActionCardProps {
  syncAction: SyncActionStatus;
  isStarting: boolean;
  isStopping: boolean;
  onRun: () => void;
  onStop: (runId: string) => void;
}

export const CmtSyncActionCard = ({
  syncAction,
  isStarting,
  isStopping,
  onRun,
  onStop,
}: Readonly<CmtSyncActionCardProps>) => {
  const { lastRun, lastSuccess, running } = syncAction;
  const messages = syncActionMessages[syncAction.action];

  return (
    <Card
      className={styles.card}
      title={<FormattedMessage {...messages.label} />}
      actions={[
        <Button
          key="run"
          type="primary"
          className={styles.runButton}
          loading={isStarting || running}
          disabled={running}
          onClick={onRun}
        >
          {running ? (
            <FormattedMessage
              id="settings.button.running"
              defaultMessage="Running..."
            />
          ) : (
            <FormattedMessage id="settings.button.run" defaultMessage="Run" />
          )}
        </Button>,
        ...(running && lastRun
          ? [
              <Popconfirm
                key="stop"
                title={
                  <FormattedMessage
                    id="settings.stop.confirm.title"
                    defaultMessage="Stop this synchronisation?"
                  />
                }
                description={
                  <FormattedMessage
                    id="settings.stop.confirm.description"
                    defaultMessage="It will be marked as failed and can be run again."
                  />
                }
                okText={
                  <FormattedMessage
                    id="settings.button.stop"
                    defaultMessage="Stop"
                  />
                }
                okButtonProps={{ danger: true }}
                cancelText={
                  <FormattedMessage
                    id="settings.button.cancel"
                    defaultMessage="Cancel"
                  />
                }
                onConfirm={() => onStop(lastRun.id)}
              >
                <Button
                  danger
                  className={styles.runButton}
                  loading={isStopping}
                >
                  <FormattedMessage
                    id="settings.button.stop"
                    defaultMessage="Stop"
                  />
                </Button>
              </Popconfirm>,
            ]
          : []),
      ]}
    >
      <Space direction="vertical" size="middle" className={styles.content}>
        <Typography.Paragraph className={styles.description}>
          <FormattedMessage {...messages.description} />
        </Typography.Paragraph>
        {lastRun ? (
          <Space direction="vertical" size={4}>
            <Space wrap>
              <Typography.Text strong>
                <FormattedMessage
                  id="settings.last_run"
                  defaultMessage="Last run"
                />
              </Typography.Text>
              <CmtSyncRunStatusTag status={lastRun.status} />
              <CmtSyncTriggerTag trigger={lastRun.trigger} />
            </Space>
            <Typography.Text>
              <CmtSyncRunStartedAt run={lastRun} />
              {" · "}
              <CmtSyncRunAuthor run={lastRun} />
              {lastRun.endedAt && (
                <>
                  {" · "}
                  <CmtSyncRunDuration run={lastRun} />
                </>
              )}
            </Typography.Text>
            {lastRun.status === "FAILURE" && (
              <Typography.Text type="secondary">
                <FormattedMessage
                  id="settings.last_success"
                  defaultMessage="Last success:"
                />{" "}
                {lastSuccess ? (
                  <CmtSyncRunStartedAt run={lastSuccess} />
                ) : (
                  <FormattedMessage
                    id="settings.never"
                    defaultMessage="never"
                  />
                )}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">
            <FormattedMessage
              id="settings.never_run"
              defaultMessage="Never run yet"
            />
          </Typography.Text>
        )}
      </Space>
    </Card>
  );
};
